import {
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Order, ShipmentStatus } from '../../../../../models/order.model';
import { Customer } from '../../../../../models/customer.model';
import { DatePipe } from '@angular/common';
import { MessageService } from 'primeng/api';
import { CarrierService } from '../../../../../services/carrier.service';
import {
  DropdownOption,
  SelectOption,
} from '../../../../../../shared/models/dropdowm.model';
import { CreateOrderShipmentRequest } from '../../models/order-details.model';
import { OrderService } from '../../../../../services/order.service';
import { HttpErrorResponse } from '@angular/common/http';
import { FormGroupService } from '../../../../../../shared/services/form-group.service';
import { ApiResult } from '../../../../../../shared/models/api-result.model';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { FormErrorComponent } from '../../../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../../../shared/components/form-server-error/form-server-error.component';
import { InputMaskModule } from 'primeng/inputmask';
import { dateValidator } from '../../../../../../shared/validators/date.validator';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
  selector: 'app-order-details-shipment',
  imports: [
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    DatePipe,
    SelectModule,
    TextareaModule,
    FormErrorComponent,
    FormServerErrorComponent,
    InputMaskModule,
    ProgressSpinnerModule,
    FormsModule,
  ],
  templateUrl: './order-details-shipment.component.html',
})
export class OrderDetailsShipmentComponent implements OnInit {
  order = input<Order | null>(null);
  customer = input<Customer | null>(null);
  readOnly = input(false);
  @Output() loadData = new EventEmitter<string>();

  private readonly fb = inject(FormBuilder);
  private readonly carrierService = inject(CarrierService);
  private readonly messageService = inject(MessageService);
  private readonly orderService = inject(OrderService);
  private readonly formGroupService = inject(FormGroupService);

  protected readonly loading = signal(false);
  protected readonly selectLoading = signal(false);
  protected readonly carriersDropdown = signal<DropdownOption[]>([]);

  shipmentStatus = ShipmentStatus.AwaitingPickup;
  protected readonly shipmentStatusOptions: SelectOption[] =
    this.orderService.getShipmentStatusDropdownOptions();

  shipment = computed(() => {
    if (this.order()?.shipments.length === 0) return null;

    return this.order()?.shipments[0];
  });

  protected readonly formGroup = this.fb.group({
    carrierId: [null, [Validators.required]],
    trackingCode: [null, [Validators.required, Validators.maxLength(255)]],
    shippedAt: [null, [Validators.required, dateValidator]],
    estimatedDeliveryDate: [null, [Validators.required, dateValidator]],
    note: [null, [Validators.maxLength(255)]],
  });

  ngOnInit(): void {
    if (this.shipment()) this.shipmentStatus = this.shipment()!.status;
    else this.loadCarriers();
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        const control = this.formGroup.get(key);
        control?.markAsTouched();
      });
      return;
    }

    let data = this.formGroup.getRawValue();
    var request: CreateOrderShipmentRequest = {
      carrierId: data.carrierId!,
      trackingCode: data.trackingCode!,
      shippedAt: this.parseBrazilianDate(data.shippedAt!),
      estimatedDeliveryDate: this.parseBrazilianDate(
        data.estimatedDeliveryDate!,
      ),
      note: data.note,
    };
    if (!this.shipment()) {
      this.create(request);
    }
  }

  updateShipmentStatus(): void {
    this.loading.set(true);

    this.orderService
      .updateStatusOrderShipment(
        this.order()!.id,
        this.shipment()!.id,
        this.shipmentStatus,
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso!',
            detail: 'O status dos dados de envio foram atualizados sucesso!',
            life: 2000,
          });

          this.loading.set(false);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            if ([400, 422].includes(e.status)) {
              if (
                (e.error.message &&
                  (e.error.errors || e.error.errors.length > 0)) ||
                (!e.error.message &&
                  (e.error.errors || e.error.errors.length > 0))
              ) {
                this.formGroupService.addServerErrors(
                  e.error.errors,
                  this.formGroup,
                );
              } else if (
                e.error.message &&
                (!e.error.errors || e.error.errors.length == 0)
              ) {
                this.messageService.add({
                  severity: 'warn',
                  summary: 'Warning',
                  detail: e.error.message,
                  life: 3000,
                });
              }
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Fail',
                detail:
                  'Ocorreu um erro ao atualizar o status os dados de envio',
                life: 3000,
              });
            }
          }
        },
      });
  }

  private create(data: CreateOrderShipmentRequest): void {
    this.loading.set(true);

    this.orderService.createOrderShipment(this.order()!.id, data).subscribe({
      next: () => {
        this.loading.set(false);

        if (this.order()) this.loadData.emit(this.order()!.id);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso!',
          detail: 'Dados de envio cadastradas com sucesso!',
          life: 2000,
        });
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup,
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Fail',
              detail: 'Ocorreu um erro ao cadastrar os dados de envio',
              life: 3000,
            });
          }
        }
      },
    });
  }

  private loadCarriers(): void {
    this.selectLoading.set(true);
    this.carrierService.getAllDropdown().subscribe({
      next: (response: ApiResult) => {
        const options = response.data as DropdownOption[];
        this.carriersDropdown.set(options ?? []);
        this.selectLoading.set(false);
      },
      error: (e) => {
        this.selectLoading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar as transportadoras',
            life: 3000,
          });
        }
      },
    });
  }

  private parseBrazilianDate(dateStr: string): string {
    const [day, month, year] = dateStr.split('/').map(Number);

    return `${year}-${month.toString().padStart(2, '0')}-${day
      .toString()
      .padStart(2, '0')}`;
  }
}
